import { cx } from "@/utils/classnames";
import React from "react";

export const DesignSystemTypography = {
  "2xs": "text-cds-2xs",
  xs: "text-cds-xs",
  sm: "text-cds-sm",
  md: "text-cds-md",
  lg: "text-cds-lg",
  xl: "text-cds-xl",
  h1: "text-cds-h1",
  h2: "text-cds-h2",
  h3: "text-cds-h3",
  h4: "text-cds-h4",
  h5: "text-cds-h5",
  h6: "text-cds-h6",
};

export type H1Props = React.ComponentPropsWithoutRef<"h1">;
export type H2Props = React.ComponentPropsWithoutRef<"h2">;
export type H3Props = React.ComponentPropsWithoutRef<"h3">;
export type H4Props = React.ComponentPropsWithoutRef<"h4">;
export type H5Props = React.ComponentPropsWithoutRef<"h5">;
export type H6Props = React.ComponentPropsWithoutRef<"h6">;
export type PProps = React.ComponentPropsWithoutRef<"p">;

export const H1: React.FC<H1Props> = ({ className, children, ...rest }: H1Props) => {
  return (
    <h1
      className={cx(
        "text-cds-ink-strong dark:text-inherit",
        "[&.text-zinc-500]:!text-cds-ink-subtle dark:[&.text-zinc-500]:!text-zinc-500",
        "[&_.text-zinc-500]:!text-cds-ink-subtle dark:[&_.text-zinc-500]:!text-zinc-500",
        className,
        DesignSystemTypography["h1"],
      )}
      {...rest}
    >
      {children}
    </h1>
  );
};

export const H2: React.FC<H2Props> = ({ className, children, ...rest }: H2Props) => {
  return (
    <h2
      className={cx(
        "text-cds-ink-strong dark:text-inherit",
        className,
        DesignSystemTypography["h2"],
      )}
      {...rest}
    >
      {children}
    </h2>
  );
};

export const H3: React.FC<H3Props> = ({ className, children, ...rest }: H3Props) => {
  return (
    <h3 className={cx(className, DesignSystemTypography["h3"])} {...rest}>
      {children}
    </h3>
  );
};

export const H4: React.FC<H4Props> = ({ className, children, ...rest }: H4Props) => {
  return (
    <h4 className={cx(className, DesignSystemTypography["h4"])} {...rest}>
      {children}
    </h4>
  );
};

export const CDSH5: React.FC<H5Props> = ({ className, children, ...rest }: H5Props) => {
  return (
    <h5 className={cx(className, DesignSystemTypography["h5"])} {...rest}>
      {children}
    </h5>
  );
};

export const CDSH6: React.FC<H6Props> = ({ className, children, ...rest }: H6Props) => {
  return (
    <h6 className={cx(className, DesignSystemTypography["h6"], "font-[450]")} {...rest}>
      {children}
    </h6>
  );
};

export const P: React.FC<PProps> = ({ className, ...rest }: PProps) => {
  return (
    <p className={cx("text-cds-sm text-cds-ink-subtle dark:text-zinc-400", className)} {...rest} />
  );
};

export type TextVariant = "2xs" | "xs" | "sm" | "md" | "lg" | "xl";

type TextProps<E extends React.ElementType = "span"> = {
  as?: E;
  size?: TextVariant;
} & Omit<React.ComponentPropsWithoutRef<E>, "as">;

export const CDSText = <E extends React.ElementType = "span">({
  as,
  size = "md",
  className,
  ...rest
}: TextProps<E>) => {
  const Component = as ?? "span";
  return <Component className={cx(className, DesignSystemTypography[size])} {...rest} />;
};
